// One microphone stream from the check-in (mic test, Start tap) to the call, so
// the owner is asked once. Ported from ottos-consult
// src/consult/microphonePreflight.js (PORTED-FROM.md): the call takes the stream
// over and then owns and stops it.
import { MicSettings } from "@otto/config";

export type MicProblem = "denied" | "blocked_by_system" | "unavailable" | "no_device" | "in_use" | "error";

export type MicResult = { granted: true; stream: MediaStream } | { granted: false; problem: MicProblem; name: string };

let heldStream: MediaStream | null = null;
let pending: Promise<MicResult> | null = null;

function live(stream: MediaStream | null): stream is MediaStream {
  return Boolean(stream?.getAudioTracks().some((track) => track.readyState === "live"));
}

/** The constraints come from @otto/config MicSettings (echo cancellation, noise suppression, auto gain on). */
export function micConstraints(settings: MicSettings = MicSettings.parse({})): MediaStreamConstraints {
  return {
    audio: {
      echoCancellation: settings.echo_cancellation,
      noiseSuppression: settings.noise_suppression,
      autoGainControl: settings.auto_gain_control,
    },
  };
}

export function problemFrom(error: unknown): { problem: MicProblem; name: string } {
  const name =
    error && typeof error === "object" && "name" in error ? String((error as { name: unknown }).name) : "Error";
  const message =
    error && typeof error === "object" && "message" in error ? String((error as { message: unknown }).message) : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return { problem: /system/i.test(message) ? "blocked_by_system" : "denied", name };
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") return { problem: "no_device", name };
  if (name === "NotReadableError" || name === "AbortError") return { problem: "in_use", name };
  return { problem: "error", name };
}

export async function requestMicrophone(settings?: MicSettings): Promise<MicResult> {
  if (live(heldStream)) return { granted: true, stream: heldStream };
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { granted: false, problem: "unavailable", name: "Unavailable" };
  }
  if (pending) return pending;
  pending = navigator.mediaDevices
    .getUserMedia(micConstraints(settings))
    .then((stream): MicResult => {
      heldStream = stream;
      return { granted: true, stream };
    })
    .catch((error: unknown): MicResult => ({ granted: false, ...problemFrom(error) }))
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** Hand the held stream to the call, which then owns it. */
export function takeMicrophoneStream(): MediaStream | null {
  const stream = live(heldStream) ? heldStream : null;
  heldStream = null;
  return stream;
}

export function releaseMicrophone(): void {
  heldStream?.getTracks().forEach((track) => track.stop());
  heldStream = null;
}

/** "granted", "denied", "prompt" or "unknown" (Safari may not answer), without prompting. */
export async function micPermissionState(): Promise<"granted" | "denied" | "prompt" | "unknown"> {
  try {
    const status = await navigator.permissions?.query({ name: "microphone" as PermissionName });
    return status?.state ?? "unknown";
  } catch {
    return "unknown";
  }
}
